import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type {
	GetAIHistoryFiltersResult,
	GetAIHistoryRequest,
	GetAIHistoryResults,
	AIHistoryData,
	ReportRequestRow,
	RequestOgmAiHistoryReportArgs,
	HistoryType,
} from '~/ogm-hub';
import {
	OptimizationHistoryController,
	OptimizationStatus,
	ReportController,
} from '~/ogm-hub';
import * as SuggestionsSlice from '~/Slices/OGMAI/SuggestionsSlice';
import * as SuppliersSlice from '../SuppliersSlice';

export interface IFilterOption {
	label: string;
	value: number;
}

export interface IOptimizationHistoryState {
	searchTerm: string;
	orderGuideFilter: IFilterOption;
	orderGuideFilters: IFilterOption[];
	orderGuideFilterOptions: IFilterOption[];
	distributionCenterFilter: IFilterOption;
	distributionCenterFilterOptions: IFilterOption[];
	historyTypeFilter: IFilterOption;
	startDate: string;
	endDate: string;
	optimizationHistoryData: AIHistoryData[];
	histDeleteData?: HistDeleteData | null;
	historyPage: number;
	totalOptimizationHistoryDataLength: number;
	totalGroupingOptimizationHistoryDataLength: number;
	isFetchingFilters: boolean;
	isFetchingHistoryData: boolean;
	isDeletingHistoryData: boolean;
	selectedProductKey: string;
}

export enum SearchFilterType {
	DisplayOrderGuideNames,
	SearchTerm,
	OrderGuide,
	DistributionCenter,
	Date,
}

export interface SearchFilterChangedArgs {
	type: SearchFilterType;
	label: string | null;
	value: string | null;
}

export interface GetProductDetailsParams {
	supplierId: number;
	productNumber: string;
}

export interface HistDeleteData {
	historyId: number;
	historyType: HistoryType;
}

export const onExportReport = createAsyncThunk<
	ReportRequestRow,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'historypage/requestReport',
	async (_, { getState, rejectWithValue, dispatch }) => {
		let response = null;
		try {
			const currentState = getState().optimizationHistory;
			const startDate = currentState.startDate;
			const endDate = currentState.endDate;

			const OgmAiHistoryReportRequestArgs: RequestOgmAiHistoryReportArgs = {
				orderGuideFilters: currentState.orderGuideFilters
					? currentState.orderGuideFilters.map((x) => x.value.toString())
					: [],
				distributionCenterFilter: currentState.distributionCenterFilter
					? currentState.distributionCenterFilter.value.toString()
					: '',
				searchTerm: currentState.searchTerm,
				dateFilter: '',
				startDate: startDate,
				endDate: endDate,
			};

			response = await unwrapFetch2(
				ReportController.RequestOgmAiHistoryReport(
					OgmAiHistoryReportRequestArgs
				)
			);
			if (response.errors.length === 0) {
				reportSuccessfullyRequested();
				dispatch(push('/reports'));
			} else {
				unknownErrorRequestingReport();
			}
		} catch (err) {
			if (err.message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
				return rejectWithValue(response.errors);
			}
		}
	}
);

export const onSearchSubmit = createAsyncThunk<
	GetAIHistoryResults,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('history/onSubmit', async (_: void, { getState, rejectWithValue }) => {
	try {
		const currentState = getState().optimizationHistory;
		const startDate = currentState.startDate;
		const endDate = currentState.endDate;
		const currentPage = currentState.historyPage;

		const requestArgs: GetAIHistoryRequest = {
			searchTerm: currentState.searchTerm,
			orderGuideFilters: currentState.orderGuideFilters
				? currentState.orderGuideFilters.map((x) => x.value)
				: null,
			distributionCenterFilters: currentState.distributionCenterFilter
				? [currentState.distributionCenterFilter.value]
				: null,
			historyType: currentState.historyTypeFilter
				? currentState.historyTypeFilter.value
				: null,
			startDate: startDate,
			endDate: endDate,
			page: currentPage,
			dateFilter: null,
			status: OptimizationStatus.Accepted,
		};

		return await unwrapFetch2(
			OptimizationHistoryController.GetAIHistory(requestArgs)
		);
	} catch (err) {
		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export const onHistoryDelete = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'optimizationHistory/onHistoryDelete',
	async (_: void, { getState, rejectWithValue, dispatch }) => {
		try {
			const currentState = getState().optimizationHistory;

			await unwrapFetch2(
				OptimizationHistoryController.DeleteAIHistory(
					currentState.histDeleteData.historyId,
					currentState.histDeleteData.historyType
				)
			);

			dispatch(onSearchSubmit());
			toastr.success(
				'History Entry Deleted',
				'The history entry was successfully deleted.'
			);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const onGetFilters = createAsyncThunk<
	GetAIHistoryFiltersResult,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'optimizationHistory/onGetFilters',
	async (_: void, { getState, dispatch, rejectWithValue }) => {
		try {
			const orderGuideFilters =
				getState().optimizationHistory.orderGuideFilters;

			const result = await unwrapFetch2(
				OptimizationHistoryController.GetAIHistoryFilters({
					orderGuideFilters: orderGuideFilters
						? orderGuideFilters.map(
								(orderGuideFilter) => orderGuideFilter.value
						  )
						: null,
				})
			);
			dispatch(SuppliersSlice.fetchCurrentCustomerSuppliers());
			dispatch(onSearchSubmit());
			return result;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

const unknownErrorRequestingReport = () => {
	toastr.error(
		'Report Request Failed',
		'The report was not successfully requested.'
	);
};
const reportCurrentlyInProgress = () => {
	toastr.warning(
		'Report Request Failed',
		'Another report request is currently in progress'
	);
};
const reportSuccessfullyRequested = () => {
	toastr.success(
		'Report Request Sent',
		'The report was successfully requested.'
	);
};

const historyDataAdapter = createEntityAdapter<AIHistoryData>({
	selectId: (historyData) => historyData.metaProductId,
});

const initialState: IOptimizationHistoryState = {
	searchTerm: '',
	orderGuideFilter: null,
	orderGuideFilters: null,
	orderGuideFilterOptions: [],
	distributionCenterFilter: null,
	distributionCenterFilterOptions: [],
	historyTypeFilter: null,
	startDate: null,
	endDate: null,
	optimizationHistoryData: [],
	histDeleteData: null,
	historyPage: 0,
	totalOptimizationHistoryDataLength: 0,
	totalGroupingOptimizationHistoryDataLength: 0,
	isFetchingFilters: true,
	isFetchingHistoryData: true,
	isDeletingHistoryData: false,
	selectedProductKey: '',
};

export function selectHistoryData(
	state: EntityState<AIHistoryData>
): AIHistoryData[] {
	return historyDataAdapter.getSelectors().selectAll(state);
}

export const OGMAIHistorySlice = createSlice({
	name: 'OGMAIHistory',
	initialState,
	reducers: {
		selectedProductKeyChange: (state, action) => {
			state.selectedProductKey = action.payload;
		},
		onSearchTermChange: (state, action) => {
			state.searchTerm = action.payload;
		},
		onOrderGuideFilterChange: (state, action) => {
			state.orderGuideFilters = action.payload;
		},
		onDistributionCenterFilterChange: (state, action) => {
			state.distributionCenterFilter = action.payload;
		},
		onHistoryTypeFilterChange: (state, action) => {
			state.historyTypeFilter = action.payload;
		},
		onStartDateSelection: (state, action) => {
			state.startDate = action.payload;
		},
		onEndDateSelection: (state, action) => {
			state.endDate = action.payload;
		},
		onPageChange: (state, action) => {
			state.historyPage = action.payload;
		},
		onOptimizationHistoryDeleteInitiated: (state, action) => {
			state.histDeleteData = action.payload;
		},
		onOptimizationHistoryDeleteCancelled: (state) => {
			state.histDeleteData = null;
		},
		onPageNavigation: () => {
			return initialState;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(onSearchSubmit.pending, (state) => {
			state.isFetchingHistoryData = true;
		});
		builder.addCase(onSearchSubmit.fulfilled, (state, action) => {
			state.optimizationHistoryData = action.payload.data;
			state.totalOptimizationHistoryDataLength = action.payload.totalResults;
			state.isFetchingHistoryData = false;
		});
		builder.addCase(onHistoryDelete.pending, (state) => {
			state.isDeletingHistoryData = true;
		});
		builder.addCase(onHistoryDelete.fulfilled, (state) => {
			state.isDeletingHistoryData = false;
			state.histDeleteData = null;
		});
		builder.addCase(onGetFilters.pending, (state) => {
			state.isFetchingFilters = true;
		});
		builder.addCase(onGetFilters.fulfilled, (state, action) => {
			state.orderGuideFilterOptions = action.payload.orderGuideFilters;
			state.distributionCenterFilterOptions =
				action.payload.distributionCenterFilters;
			state.distributionCenterFilter = null;
			state.isFetchingFilters = false;
		});
		builder.addCase(SuggestionsSlice.getOptimizations.pending, (state) => {
			state.orderGuideFilter = initialState.orderGuideFilter;
			state.distributionCenterFilter = initialState.distributionCenterFilter;
			state.startDate = initialState.startDate;
			state.endDate = initialState.endDate;
			state.searchTerm = initialState.searchTerm;
			state.historyTypeFilter = initialState.historyTypeFilter;
		});
	},
});

export const {
	onSearchTermChange,
	onOrderGuideFilterChange,
	onDistributionCenterFilterChange,
	onHistoryTypeFilterChange,
	onStartDateSelection,
	onEndDateSelection,
	onOptimizationHistoryDeleteInitiated,
	onOptimizationHistoryDeleteCancelled,
	onPageChange,
	onPageNavigation,
} = OGMAIHistorySlice.actions;
